import { envConfig } from "@configs/env";
import { CURRENCY_CONFIGS, STORAGE_KEY } from "@constants";
import { store } from "@stores";
import { Currency, CurrencyDirection, DeployCountry, MemberSettings, _MemberSettingOnboardDialogLayout } from "@types";
import { InfoResponse, MemberSettingAuthMode, MemberSettingFundingMode, MemberSettingNameMode } from "kz-ui-sdk";
import logger from "./logger";

const DIALOG_LAYOUT_DEV = localStorage.getItem("DIALOG_LAYOUT_DEV") as _MemberSettingOnboardDialogLayout | null;

// At first hour after deployment, /info may be cached in browser, so we need to fallback to default hardcoded settings first
const FALLBACK_MEMBER_SETTINGS: Record<
  DeployCountry,
  Pick<MemberSettings, "languageList" | "language" | "authMode" | "fundingMode" | "nameMode" | "onboardDialogLayout">
> = {
  [DeployCountry.PH]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    languageList: ["en"],
    language: "en",
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
  },
  [DeployCountry.TH]: {
    authMode: MemberSettingAuthMode.PhonePin,
    fundingMode: MemberSettingFundingMode.BankOnly,
    nameMode: MemberSettingNameMode.FirstLast,
    languageList: ["en", "th"],
    language: "th",
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_DRAWER,
  },
};

// fallback member settings mode
const fallbackMemberSettings = FALLBACK_MEMBER_SETTINGS[envConfig.countryCode as DeployCountry];

const DEFAULT_MEMBER_SETTINGS: MemberSettings = {
  themeId: import.meta.env.VITE_DEV_THEME_PRESET || import.meta.env.VITE_THEME_PRESET,
  phonePrefix: "",
  phoneLength: 0,
  currency: "",
  currencyDirection: CurrencyDirection.SUFFIX,
  currencySymbol: "",
  language: "",
  languageList: fallbackMemberSettings.languageList,
  countryCode: "",
  authMode: fallbackMemberSettings.authMode,
  fundingMode: fallbackMemberSettings.fundingMode,
  nameMode: fallbackMemberSettings.nameMode,
  onboardDialogLayout: fallbackMemberSettings.onboardDialogLayout,
  isLoaded: false,
};

/**
 * Build & persist member settings from server info
 */
export const buildMemberSettings = (serverInfo: InfoResponse): MemberSettings => {
  // currency config
  const currencyCode = serverInfo?.currency ?? DEFAULT_MEMBER_SETTINGS.currency;
  let currencyConfig = null;
  if (currencyCode) {
    currencyConfig = CURRENCY_CONFIGS[currencyCode];
  }
  // phone length
  // add 1 for the leading 0
  const phoneLength = (serverInfo?.phoneSpec?.length ?? DEFAULT_MEMBER_SETTINGS.phoneLength) + 1;

  const memberSettings: MemberSettings = {
    themeId: serverInfo?.theme?.color ?? DEFAULT_MEMBER_SETTINGS.themeId,
    // phone config
    phonePrefix: serverInfo?.phoneSpec?.phonePrefix ?? DEFAULT_MEMBER_SETTINGS.phonePrefix,
    phoneLength,
    // language config
    language: serverInfo?.languageCode ?? DEFAULT_MEMBER_SETTINGS.language,
    languageList: serverInfo?.languageList ?? DEFAULT_MEMBER_SETTINGS.languageList,
    // currency config
    currency: currencyCode as Currency,
    currencyDirection: currencyConfig?.direction ?? DEFAULT_MEMBER_SETTINGS.currencyDirection,
    currencySymbol: currencyConfig?.symbol ?? DEFAULT_MEMBER_SETTINGS.currencySymbol,
    // country config
    countryCode: serverInfo?.countryCode ?? DEFAULT_MEMBER_SETTINGS.countryCode,
    // feature flags
    authMode: serverInfo?.memberSettings?.authMode ?? DEFAULT_MEMBER_SETTINGS.authMode,
    fundingMode: serverInfo?.memberSettings?.fundingMode ?? DEFAULT_MEMBER_SETTINGS.fundingMode,
    nameMode: serverInfo?.memberSettings?.nameMode ?? DEFAULT_MEMBER_SETTINGS.nameMode,
    onboardDialogLayout: DIALOG_LAYOUT_DEV || DEFAULT_MEMBER_SETTINGS.onboardDialogLayout,
    isLoaded: !!serverInfo,
  };

  // If language is not in languageList, use first language from list or fallback to default
  if (!memberSettings.languageList?.includes(memberSettings.language)) {
    memberSettings.language = memberSettings.languageList[0] || DEFAULT_MEMBER_SETTINGS.language;
  }

  // override themeId from dev environment
  const devThemePreset = import.meta.env.VITE_DEV_THEME_PRESET;
  if (devThemePreset) {
    memberSettings.themeId = devThemePreset;
  }

  persistMemberSettings(memberSettings);
  return memberSettings;
};

export const persistMemberSettings = (memberSettings: MemberSettings) => {
  localStorage.setItem(STORAGE_KEY.MEMBER_SETTINGS, JSON.stringify(memberSettings));
};

/**
 * Get member settings from local storage
 * Use for internal use, fallback to default settings if not found
 * @returns MemberSettings
 */
export const getStoredMemberSettings = (): MemberSettings => {
  const storedSettings = localStorage.getItem(STORAGE_KEY.MEMBER_SETTINGS);
  if (storedSettings) {
    return JSON.parse(storedSettings) as MemberSettings;
  }
  return DEFAULT_MEMBER_SETTINGS;
};

/**
 * Get member settings from Redux state
 * @returns MemberSettings
 */
export const getReduxMemberSettings = (): MemberSettings | null => {
  try {
    if (store && store.getState) {
      const state = store.getState();
      // Check if preferences slice exists in state
      if (state && state.preferences && state.preferences.memberSettings) {
        return state.preferences.memberSettings;
      }
    }
    logger._console.error("Failed to get member settings from Redux state, fallback to local storage");
    return null;
  } catch (error) {
    logger._console.error("Failed to get member settings from Redux state, fallback to local storage");
    return null;
  }
};

/**
 * Get member settings from Redux state or local storage
 * @returns MemberSettings
 */
export const getMemberSettings = (): MemberSettings => {
  return getReduxMemberSettings() || getStoredMemberSettings();
};
